import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export type KindnessConfig = { enabled: boolean; minPence: number; maxPence: number };
export type KindnessDisplay = "anonymous" | "named";

/** Result returned when the modal resolves. contributionPence is 0 when declined. */
export type KindnessResult = { contributionPence: number; display: KindnessDisplay };

function pence(p: number) { return `£${(p / 100).toFixed(2)}`; }

/**
 * Random Act of Kindness feature config.
 *
 * Fetches GET /api/kindness/config once on mount. Returns `null` until loaded
 * and whenever the feature is disabled or the request fails, so callers treat
 * "not enabled" and "not yet known" identically — the feature stays invisible
 * and behaviour is byte-for-byte what it was before the flag existed.
 */
export function useKindnessConfig(): KindnessConfig | null {
  const [config, setConfig] = useState<KindnessConfig | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/kindness/config")
      .then((r) => (r.ok ? (r.json() as Promise<KindnessConfig>) : null))
      .then((d) => {
        if (cancelled || !d || !d.enabled) return;
        if (typeof d.minPence !== "number" || typeof d.maxPence !== "number") return;
        setConfig(d);
      })
      .catch(() => { /* feature stays invisible on error */ });
    return () => { cancelled = true; };
  }, []);
  return config;
}

/** The preset "pay it forward" amounts, filtered to those within the configured range. */
function presetPills(config: KindnessConfig): number[] {
  return [100, 200, 500].filter((p) => p >= config.minPence && p <= config.maxPence);
}

export function KindnessModal({
  open, config, onResolve,
}: {
  open: boolean;
  config: KindnessConfig;
  /** Called with the chosen amount (0 = declined) when either button is tapped. */
  onResolve: (result: KindnessResult) => void;
}) {
  const pills = presetPills(config);
  const [selectedPence, setSelectedPence] = useState<number | null>(null);
  const [otherInput, setOtherInput] = useState("");
  const [usingOther, setUsingOther] = useState(false);
  const [display, setDisplay] = useState<KindnessDisplay>("named");
  const [error, setError] = useState<string | null>(null);

  // Reset to a clean state each time the modal opens so a re-open never shows a
  // stale amount. (It is only prompted once per attempt, but defensive.)
  useEffect(() => {
    if (open) {
      setSelectedPence(null);
      setOtherInput("");
      setUsingOther(false);
      setDisplay("named");
      setError(null);
    }
  }, [open]);

  function parseOther(): number | null {
    const trimmed = otherInput.trim();
    if (!trimmed) return null;
    // Pounds, with up to two decimal places — convert to integer pence.
    if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
    const asPence = Math.round(parseFloat(trimmed) * 100);
    return Number.isInteger(asPence) ? asPence : null;
  }

  function chosenPence(): number | null {
    if (usingOther) return parseOther();
    return selectedPence;
  }

  function handleAdd() {
    const amount = chosenPence();
    if (amount == null) {
      setError(`Please enter an amount between ${pence(config.minPence)} and ${pence(config.maxPence)}.`);
      return;
    }
    if (amount < config.minPence || amount > config.maxPence) {
      setError(`Amount must be between ${pence(config.minPence)} and ${pence(config.maxPence)}.`);
      return;
    }
    onResolve({ contributionPence: amount, display });
  }

  function handleDecline() {
    onResolve({ contributionPence: 0, display });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleDecline(); }}>
      <DialogContent className="max-w-md rounded-3xl max-h-[92dvh] overflow-y-auto [&>button]:min-h-11 [&>button]:min-w-11 [&>button]:grid [&>button]:place-items-center">
        <DialogHeader>
          <DialogTitle className="text-primary">Random Act of Kindness </DialogTitle>
          <DialogDescription>
            Fancy helping pay for someone else's order? Whatever you give will come off the next
            person's order on Ayrshire Hub. They'll see that someone paid it forward to brighten their day.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <p className="text-sm font-semibold text-slate-800 mb-2">How much would you like to pay forward?</p>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Choose an amount to pay forward">
              {pills.map((p) => {
                const active = !usingOther && selectedPence === p;
                return (
                  <button
                    key={p}
                    type="button"
                    aria-pressed={active}
                    onClick={() => { setUsingOther(false); setSelectedPence(p); setError(null); }}
                    className={`px-4 py-2 rounded-xl text-sm font-bold border-2 transition-colors ${
                      active ? "bg-primary border-primary text-white" : "bg-white text-slate-700 border-slate-200 hover:border-primary/50"
                    }`}
                  >
                    {pence(p)}
                  </button>
                );
              })}
              <button
                type="button"
                aria-pressed={usingOther}
                onClick={() => { setUsingOther(true); setSelectedPence(null); setError(null); }}
                className={`px-4 py-2 rounded-xl text-sm font-bold border-2 transition-colors ${
                  usingOther ? "bg-primary border-primary text-white" : "bg-white text-slate-700 border-slate-200 hover:border-primary/50"
                }`}
              >
                Other amount
              </button>
            </div>
            {usingOther && (
              <div className="mt-2">
                <label htmlFor="kindness-other" className="sr-only">Other amount in pounds</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
                  <input
                    id="kindness-other"
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={otherInput}
                    onChange={(e) => { setOtherInput(e.target.value); setError(null); }}
                    placeholder="0.00"
                    aria-describedby={error ? "kindness-error" : undefined}
                    aria-invalid={!!error}
                    className="w-full border border-slate-200 rounded-xl pl-7 pr-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/60"
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Between {pence(config.minPence)} and {pence(config.maxPence)}.
                </p>
              </div>
            )}
            {error && <p id="kindness-error" className="text-xs text-red-500 mt-2">{error}</p>}
          </div>

          <fieldset>
            <legend className="text-sm font-semibold text-slate-800 mb-2">How would you like to appear?</legend>
            <div className="space-y-2">
              {([
                { value: "anonymous" as const, label: "Keep me anonymous" },
                { value: "named" as const, label: "Show my name" },
              ]).map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 cursor-pointer transition-colors ${
                    display === opt.value ? "border-primary bg-primary/5" : "border-slate-200 hover:border-primary/40"
                  }`}
                >
                  <input
                    type="radio"
                    name="kindness-display"
                    value={opt.value}
                    checked={display === opt.value}
                    onChange={() => setDisplay(opt.value)}
                    className="accent-[#f0a500]"
                  />
                  <span className="text-sm font-medium text-slate-800">{opt.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={handleDecline}>
            No thanks
          </Button>
          <Button
            type="button"
            className="bg-primary hover:bg-primary/90 text-white font-bold"
            onClick={handleAdd}
          >
            Add to my order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
